'use client';

import { c as _c } from "react/compiler-runtime";
import { BlocksContent } from '@strapi/blocks-react-renderer';
import clsx from 'clsx';
import { FC, useLayoutEffect, useRef } from 'react';
import MediaInfoPreview from '@/components/CaseStudy/MediaInfoGrid/MediaInfoPreview';
import StrapiRichContent from '@/components/StrapiRichContent';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap, ScrollTrigger } from '@/lib/gsap';
import { StrapiImage, StrapiVideo } from '@/types/common';
import css from './style.module.scss';
interface Props {
  data: {
    title?: BlocksContent;
    items: {
      preview?: StrapiImage | StrapiVideo;
      videoUrl?: string;
      videoThumbnailUrl?: string;
      title: BlocksContent;
      text: BlocksContent;
      label: string;
      mediaPosition?: 'left' | 'right';
      alt?: string;
    }[];
    maxWidth?: 'width550' | 'width600' | 'width650' | 'width700' | 'width750' | 'width800';
    withTopLine: boolean;
    isBlog?: boolean;
  };
}
const MediaInfoGrid: FC<Props> = t0 => {
  const $ = _c(25);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data?.items) {
    t1 = data?.items || [];
    $[0] = data?.items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const items = t1;
  const maxWidth = data?.maxWidth || "width800";
  const withTopLine = data?.withTopLine;
  const isBlog = data?.isBlog;
  const title = data?.title;
  const sectionRef = useRef(null);
  const shouldLoadVideo = useLazyVideoSource(sectionRef);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [sectionRef];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useViewportVideosPlayback(t2);
  const emptyTitle = !title || title?.[0]?.children?.[0]?.type === "text" && title[0].children[0].text === "";
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const section = sectionRef?.current;
      if (!section) {
        return;
      }
      const mm = gsap.matchMedia();
      mm.add({
        isDesktop: "(min-width: 992px)"
      }, context => {
        const {
          isDesktop
        } = context.conditions as gsap.Conditions;
        if (!isDesktop) {
          return;
        }
        const items_0 = section.querySelectorAll(`.${css.item}`);
        const title_0 = section.querySelector(`.${css.title}`);
        if (title_0) {
          gsap.set(title_0, {
            yPercent: 80,
            opacity: 0
          });
          gsap.to(title_0, {
            yPercent: 0,
            opacity: 1,
            duration: 0.6,
            ease: "power2.out",
            scrollTrigger: {
              trigger: title_0,
              start: "top 85%",
              end: "top 70%",
              toggleActions: "play none none reverse"
            }
          });
        }
        items_0.forEach(_temp2);
        return _temp4;
      });
      return () => {
        mm.revert();
      };
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useLayoutEffect(t3, t4);
  const t5 = withTopLine && css.withTopLine;
  let t6;
  if ($[5] !== t5) {
    t6 = clsx(css.wrapper, t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== emptyTitle || $[8] !== maxWidth || $[9] !== title) {
    t7 = title && !emptyTitle ? <div className={clsx(css.title, css[maxWidth])}><StrapiRichContent content={title} /></div> : null;
    $[7] = emptyTitle;
    $[8] = maxWidth;
    $[9] = title;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = clsx(css.content);
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== isBlog || $[13] !== items || $[14] !== shouldLoadVideo) {
    let t10;
    if ($[16] !== isBlog || $[17] !== shouldLoadVideo) {
      t10 = (item_0, index) => {
        const mediaRight = item_0?.mediaPosition === "right" ? css.mediaRight : "";
        return <div key={index} className={clsx(css.item, mediaRight)}><div className={css.media}>{item_0.preview || item_0.videoUrl ? <MediaInfoPreview preview={item_0.preview} videoUrl={item_0.videoUrl} videoThumbnailUrl={item_0.videoThumbnailUrl} alt={item_0.alt} shouldLoadVideo={shouldLoadVideo} /> : null}</div><div className={css.event}>{item_0?.label ? <p className={css.label}>{item_0?.label}</p> : null}{item_0?.title ? <div className={clsx(css.titleItem, isBlog && css.titleBlog)}><StrapiRichContent content={item_0?.title} /></div> : null}{item_0?.text ? <div className={clsx(css.text, isBlog && css.textBlog)}><StrapiRichContent content={item_0?.text} /></div> : null}</div></div>;
      };
      $[16] = isBlog;
      $[17] = shouldLoadVideo;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    t9 = items.map(t10);
    $[12] = isBlog;
    $[13] = items;
    $[14] = shouldLoadVideo;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t6 || $[23] !== t7) {
    t11 = <section ref={sectionRef} className={t6}>{t7}{t10}</section>;
    $[21] = t10;
    $[22] = t6;
    $[23] = t7;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
};
export default MediaInfoGrid;
function _temp(el) {
  gsap.set(el, {
    yPercent: 80,
    opacity: 0
  });
  gsap.to(el, {
    yPercent: 0,
    opacity: 1,
    duration: 0.6,
    ease: "power2.out",
    scrollTrigger: {
      trigger: el,
      start: "top 90%",
      end: "top 60%",
      toggleActions: "play none none reverse"
    }
  });
}
function _temp2(item) {
  const paragraphs = item.querySelectorAll(`.${css.label}, .${css.titleItem}, .${css.text}`);
  if (paragraphs.length > 0) {
    paragraphs.forEach(_temp);
  }
}
function _temp3(t) {
  return t.kill();
}
function _temp4() {
  ScrollTrigger.getAll().forEach(_temp3);
}